import { useMarketingViewSection } from '../components/MarketingViewsProvider';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Download } from 'lucide-react';
import { bingDashboardApi, type BingDateGrain, type BingOverviewRow } from '../api/bingDashboard';
import { ColumnToggle } from '../components/ColumnToggle';
import { DashboardQueryActions } from '../components/DashboardQueryActions';
import { alignMarketingDateRange, getCurrentCalendarRange, DELIVERY_RANGE_PRESETS, MarketingTimeRangeFilter } from '../components/MarketingTimeRangeFilter';
import { SortableHeader, useTableSort, type SortValue } from '../components/SortableTable';
import { TableLoadingCell, TableLoadingOverlay } from '../components/TableLoadingOverlay';
import { MetricDescriptionTooltip } from '../components/MetricDescriptionTooltip';
import { sf, type FieldKey } from '../lib/fieldPolicy';
import useAppStore from '../store/useAppStore';
import { useDownloadCenterStore } from '../store/useDownloadCenterStore';
import { useToastStore } from '../store/useToastStore';
import VooglamBingDeliveryDashboard from './VooglamBingDeliveryDashboard';
import { ZeeloolBingReportSections } from './ZeeloolBingReportDashboard';

type DatePreset = 'today' | 'yesterday' | '7d' | '14d' | '30d' | 'thisWeek' | 'thisMonth' | 'custom';

interface AppliedDateRange {
  startDate: string;
  endDate: string;
  dateGrain: BingDateGrain;
}

interface RegionOption {
  key: string;
  label: string;
  siteId: string;
  siteCode: string;
}

interface MetricField {
  key: keyof BingOverviewRow;
  label: string;
  fmt: (v: number) => string;
  permKey?: FieldKey;
}

const REGION_ORDER = ['us', 'ca', 'uk', 'de'];
const REGION_LABEL: Record<string, string> = {
  us: 'US',
  ca: 'CA',
  uk: 'UK',
  de: 'DE',
};

const DATE_GRAINS: Array<{ value: BingDateGrain; label: string }> = [
  { value: 'day', label: '日' },
  { value: 'week', label: '周' },
  { value: 'month', label: '月' },
  { value: 'year', label: '年' },
];

const fmtNum = (v: number, d = 2) =>
  v == null ? '-' : v.toLocaleString('en-US', { minimumFractionDigits: d, maximumFractionDigits: d });
const fmtInt = (v: number) => (v == null ? '-' : Math.round(v).toLocaleString('en-US'));
const fmtPct = (v: number) => (v == null ? '-' : `${(v * 100).toFixed(2)}%`);

const METRICS: MetricField[] = [
  { key: 'spend', label: '账户花费', fmt: v => fmtNum(v), permKey: 'spend' },
  { key: 'revenue', label: '购物转化价值', fmt: v => fmtNum(v), permKey: 'revenue' },
  { key: 'impressions', label: '展示次数', fmt: fmtInt },
  { key: 'clicks', label: '点击次数', fmt: fmtInt },
  { key: 'add_to_cart_count', label: '加购次数', fmt: fmtInt },
  { key: 'purchases', label: '购物次数', fmt: fmtInt },
  { key: 'account_roi', label: '账户ROI', fmt: v => fmtNum(v), permKey: 'roas' },
  { key: 'cpa', label: '账户CPA', fmt: v => fmtNum(v), permKey: 'cpa' },
  { key: 'ctr', label: '账户CTR', fmt: fmtPct },
  { key: 'cpc', label: '账户CPC', fmt: v => fmtNum(v), permKey: 'cpc' },
  { key: 'click_add_to_cart_rate', label: '点击加购率', fmt: fmtPct },
  { key: 'click_purchase_rate', label: '点击购买率', fmt: fmtPct },
  { key: 'cost_per_add_to_cart', label: '单次加购费用', fmt: v => fmtNum(v), permKey: 'cpa' },
  { key: 'aov', label: '账户单均价', fmt: v => fmtNum(v), permKey: 'revenue' },
];

function daysAgo(n: number): string {
  const d = new Date();
  d.setDate(d.getDate() - n);
  return d.toISOString().slice(0, 10);
}

function today(): string {
  return new Date().toISOString().slice(0, 10);
}

function getPresetRange(preset: DatePreset, customStart: string, customEnd: string) {
  if (preset === 'custom') return { start: customStart, end: customEnd };
  if (preset === 'thisWeek' || preset === 'thisMonth') return getCurrentCalendarRange(preset, today());
  if (preset === 'today') return { start: today(), end: today() };
  if (preset === 'yesterday') return { start: daysAgo(1), end: daysAgo(1) };
  if (preset === '14d') return { start: daysAgo(13), end: today() };
  if (preset === '30d') return { start: daysAgo(29), end: today() };
  return { start: daysAgo(6), end: today() };
}

function regionFromSiteCode(code: string): string {
  return String(code || '').split('-')[0]?.toLowerCase() || '';
}

function ZeeloolBingDeliveryDashboard() {
  const { brand, brandId, siteId, sites, adContext, sitesLoaded } = useAppStore();
  const userLevel = adContext?.data_level ?? 1;
  const [dateGrain, setDateGrain] = useState<BingDateGrain>('day');
  const [datePreset, setDatePreset] = useState<DatePreset>('7d');
  const [customStart, setCustomStart] = useState(() => daysAgo(7));
  const [customEnd, setCustomEnd] = useState(() => today());
  const [appliedDate, setAppliedDate] = useState<AppliedDateRange>(() => ({ startDate: daysAgo(6), endDate: today(), dateGrain: 'day' }));
  const [exportQuery, setExportQuery] = useState<{ siteID: string; date: AppliedDateRange } | null>(null);
  const initialQueryKeyRef = useRef('');
  const [rows, setRows] = useState<BingOverviewRow[]>([]);
  const [summary, setSummary] = useState<BingOverviewRow | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const enqueueReportExport = useDownloadCenterStore(state => state.enqueueReportExport);
  const showToast = useToastStore(state => state.show);
  const [hiddenMetricKeys, setHiddenMetricKeys] = useState<Set<string>>(() => {
    try {
      const raw = localStorage.getItem('bing_dashboard_hidden_cols');
      if (raw) return new Set(JSON.parse(raw) as string[]);
    } catch {
      // ignore corrupted local preference
    }
    return new Set();
  });

  const currentSite = useMemo<RegionOption | null>(() => {
    if (brand !== 'zeelool') return null;
    const site = sites.find(item => item.id === siteId);
    if (!site) return null;
    const region = regionFromSiteCode(site.code);
    if (!REGION_ORDER.includes(region)) return null;
    return {
      key: region,
      label: REGION_LABEL[region] ?? region.toUpperCase(),
      siteId: site.id,
      siteCode: site.code,
    };
  }, [brand, siteId, sites]);

  const dateRange = useMemo(() => {
    return alignMarketingDateRange(getPresetRange(datePreset, customStart, customEnd), dateGrain);
  }, [customEnd, customStart, dateGrain, datePreset]);

  useEffect(() => {
    if (!brandId || !currentSite?.siteId) return;
    const key = `${brandId}:${currentSite.siteId}`;
    if (initialQueryKeyRef.current === key) return;
    initialQueryKeyRef.current = key;
    setAppliedDate({ startDate: dateRange.start, endDate: dateRange.end, dateGrain });
  }, [brandId, currentSite?.siteId, dateGrain, dateRange.end, dateRange.start]);

  const visibleMetrics = useMemo(
    () => METRICS.filter(m => !hiddenMetricKeys.has(m.key as string)),
    [hiddenMetricKeys],
  );

  const sortValueFor = useCallback((row: BingOverviewRow, key: string): SortValue => {
    if (key !== 'date_label') {
      const metric = METRICS.find(item => item.key === key);
      // Do not expose relative ordering for values that are currently masked.
      if (metric?.permKey && userLevel < 2) return undefined;
    }
    return row[key as keyof BingOverviewRow] as SortValue;
  }, [userLevel]);
  const { sort, toggleSort, sortedRows, setSort } = useTableSort<BingOverviewRow, string>(rows, sortValueFor);

  useEffect(() => {
    if (sort && sort.key !== 'date_label' && hiddenMetricKeys.has(sort.key)) setSort(null);
  }, [hiddenMetricKeys, setSort, sort]);

  const metricToggleCols = useMemo(() => METRICS.map(m => ({ key: m.key as string, label: m.label })), []);

  const toggleMetricHidden = useCallback((key: string) => {
    setHiddenMetricKeys(prev => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key); else next.add(key);
      try { localStorage.setItem('bing_dashboard_hidden_cols', JSON.stringify([...next])); } catch {}
      return next;
    });
  }, []);

  const resetMetricHidden = useCallback(() => {
    setHiddenMetricKeys(new Set());
    try { localStorage.removeItem('bing_dashboard_hidden_cols'); } catch {}
  }, []);

  const fetchData = useCallback(async () => {
    if (!brandId || !currentSite?.siteId) return;
    setLoading(true);
    setError('');
    try {
      const res = await bingDashboardApi.getOverview({
        brand_id: brandId,
        site_id: currentSite.siteId,
        start_date: appliedDate.startDate,
        end_date: appliedDate.endDate,
        date_grain: appliedDate.dateGrain,
      });
      setRows(res.data?.list ?? []);
      setSummary(res.data?.summary ?? null);
      setExportQuery({ siteID: currentSite.siteId, date: { ...appliedDate } });
    } catch (e) {
      setRows([]);
      setSummary(null);
      setExportQuery(null);
      setError(e instanceof Error ? e.message : '加载失败');
    } finally {
      setLoading(false);
    }
  }, [appliedDate, brandId, currentSite?.siteId]);

  useEffect(() => {
    if (currentSite?.siteId) {
      fetchData();
    } else if (sitesLoaded) {
      setRows([]);
      setSummary(null);
    }
  }, [currentSite?.siteId, fetchData, sitesLoaded]);

  const query = () => setAppliedDate({ startDate: dateRange.start, endDate: dateRange.end, dateGrain });
  useMarketingViewSection('page', {
    label: '页面筛选', state: { dateGrain, datePreset, customStart, customEnd, hiddenMetricKeys: [...hiddenMetricKeys] },
    restore: value => { setDateGrain(value.dateGrain); setDatePreset(value.datePreset); setCustomStart(value.customStart); setCustomEnd(value.customEnd); setHiddenMetricKeys(new Set(value.hiddenMetricKeys)); },
    loading: loading, query: () => { query(); },
    resolve: value => { const range = getPresetRange(value.datePreset, value.customStart, value.customEnd); return { ...value, customStart: range.start, customEnd: range.end }; },
  });

  const reset = () => {
    setDateGrain('day');
    setDatePreset('7d');
    setCustomStart(daysAgo(6));
    setCustomEnd(today());
    setAppliedDate({ startDate: daysAgo(6), endDate: today(), dateGrain: 'day' });
  };

  const renderMetric = (row: BingOverviewRow, metric: MetricField) => {
    const raw = metric.fmt(row[metric.key] as number);
    if (!metric.permKey) return raw;
    return sf(raw, metric.permKey, userLevel).value ?? '锁定';
  };

  const handleExport = useCallback(async () => {
    if (!brandId || !exportQuery) return;
    try {
      await enqueueReportExport({
        report_key: 'ad_dashboard_bing_overview',
        title: 'Bing投放看板',
        file_name: `Bing投放看板_${exportQuery.date.startDate}_${exportQuery.date.endDate}.csv`,
        brand_id: brandId,
        site_id: exportQuery.siteID,
        params: {
          brand_id: brandId,
          site_id: exportQuery.siteID,
          start_date: exportQuery.date.startDate,
          end_date: exportQuery.date.endDate,
          date_grain: exportQuery.date.dateGrain,
        },
        columns: [
          { key: 'date_label', label: '日期' },
          ...visibleMetrics.map(metric => ({ key: metric.key as string, label: metric.label })),
        ],
      });
      showToast('导出任务已提交，请到右上角下载中心查看', 'success');
    } catch (e) {
      showToast(e instanceof Error ? e.message : '导出任务提交失败', 'error');
    }
  }, [brandId, enqueueReportExport, exportQuery, showToast, visibleMetrics]);

  return (
    <div className="page-section visible">
      <div className="dashboard-sticky-filters">
      <div className="data-filter-card">
        <div className="data-filter-row time">
          <MarketingTimeRangeFilter grain={dateGrain} grains={DATE_GRAINS} onGrainChange={setDateGrain} preset={datePreset} presets={DELIVERY_RANGE_PRESETS} onPresetChange={setDatePreset} start={customStart} end={customEnd} max={today()} customPreset="custom" onRangeChange={({ start, end }) => { setCustomStart(start); setCustomEnd(end); }} />
          <DashboardQueryActions loading={loading} onReset={reset} onQuery={query} />
        </div>
      </div>
      </div>

      {error && (
        <div style={{ padding: '10px 14px', marginBottom: 12, borderRadius: 6, background: 'rgba(220,38,38,.08)', color: '#dc2626', fontSize: 13 }}>
          {error}
        </div>
      )}

      <div className="card" style={{ marginTop: 16, padding: 0, overflow: 'hidden' }}>
        <div style={{ padding: '10px 14px', borderBottom: '1px solid var(--border2)', display: 'flex', alignItems: 'center', gap: 12 }}>
          <span style={{ fontSize: 14, fontWeight: 700, color: 'var(--text)' }}>总览报表</span>
          <div style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: 8 }}>
            {loading && <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, color: 'var(--text3)', fontSize: 12 }}>
              <i aria-hidden="true" style={{ width: 13, height: 13, borderRadius: '50%', border: '2px solid var(--border2)', borderTopColor: 'var(--primary)', animation: 'spin .8s linear infinite' }} />
              查询中…
            </span>}
            <button
              type="button"
              className="btn btn-outline btn-sm"
              onClick={handleExport}
              disabled={loading || !brandId || !currentSite}
              title="导出当前报表"
              style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}
            >
              <Download size={14} strokeWidth={2} />
              导出
            </button>
            <ColumnToggle cols={metricToggleCols} hiddenKeys={hiddenMetricKeys} onToggle={toggleMetricHidden} onReset={resetMetricHidden} />
          </div>
        </div>
        <div className="table-wrap" style={{ position: 'relative', maxHeight: 'calc(100vh - 235px)', overflow: 'auto' }}>
          <table style={{ minWidth: 1320 }}>
            <thead style={{ position: 'sticky', top: 0, zIndex: 3 }}>
              <tr>
                <SortableHeader
                  sortKey="date_label"
                  sort={sort}
                  onSort={toggleSort}
                  style={{ position: 'sticky', left: 0, zIndex: 4, background: 'var(--surface2)', minWidth: 120 }}
                >
                  日期
                </SortableHeader>
                {visibleMetrics.map(metric => (
                  <SortableHeader
                    key={metric.key as string}
                    sortKey={metric.key as string}
                    sort={sort}
                    onSort={toggleSort}
                    align="right"
                    disabled={Boolean(metric.permKey && userLevel < 2)}
                    style={{ whiteSpace: 'nowrap' }}
                  >
                    <MetricDescriptionTooltip label={metric.label} />
                  </SortableHeader>
                ))}
              </tr>
            </thead>
            <tbody>
              {summary && (
                <tr style={{ fontWeight: 700, background: 'var(--surface)' }}>
                  <td style={{ position: 'sticky', left: 0, zIndex: 1, background: 'var(--surface)', color: 'var(--text2)' }}>总计</td>
                  {visibleMetrics.map(metric => (
                    <td key={metric.key as string} style={{ textAlign: 'right', fontVariantNumeric: 'tabular-nums', color: 'var(--text)' }}>
                      {renderMetric(summary, metric)}
                    </td>
                  ))}
                </tr>
              )}
              {loading && rows.length === 0 ? (
                <tr><td colSpan={visibleMetrics.length + 1}><TableLoadingCell /></td></tr>
              ) : !currentSite ? (
                <tr><td colSpan={visibleMetrics.length + 1} style={{ textAlign: 'center', padding: 48, color: 'var(--text3)' }}>请在左侧切换到 US / CA / UK / DE 站点查看 Bing 投放数据</td></tr>
              ) : rows.length === 0 ? (
                <tr><td colSpan={visibleMetrics.length + 1} style={{ textAlign: 'center', padding: 48, color: 'var(--text3)' }}>暂无数据</td></tr>
              ) : sortedRows.map((row, idx) => (
                <tr key={`${row.date_label}-${idx}`}>
                  <td style={{ position: 'sticky', left: 0, zIndex: 1, background: idx % 2 === 0 ? 'var(--surface2)' : 'var(--surface)', fontVariantNumeric: 'tabular-nums' }}>
                    {row.date_label}
                  </td>
                  {visibleMetrics.map(metric => (
                    <td key={metric.key as string} style={{ textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>
                      {renderMetric(row, metric)}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
          {loading && rows.length > 0 && <TableLoadingOverlay />}
        </div>
      </div>
      {currentSite && <ZeeloolBingReportSections
        brandId={brandId}
        siteId={currentSite.siteId}
        startDate={dateRange.start}
        endDate={dateRange.end}
        dateGrain={dateGrain}
        userLevel={userLevel}
        region={currentSite.key}
      />}
    </div>
  );
}

export default function BingDeliveryDashboard() {
  const brand = useAppStore(state => state.brand);
  if (brand === 'vooglam') return <VooglamBingDeliveryDashboard />;
  return <ZeeloolBingDeliveryDashboard />;
}
